import { useContext, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { equalInLC, usePlatformContext } from "@datassential/platform-ui-lib";
import LegacyWorkspace from "./variations/LegacyWorkspace";
import { WORKSPACE_COMPONENTS, WORKSPACE_DEFAULT_SECTIONS } from "./variations/config";
import { HomeWorkspaceProvider } from "./context/HomeWorkspaceContext";
import { CHAIN_ENABLED_WORKSPACES, parseWorkspaceId } from "@models/Workspace";
import { AppContext } from "../../AppContext";
import { useWorkspaceResolution } from "./hooks/useWorkspaceResolution";

interface RouteParams {
    workspaceId?: string;
}

const WorkspaceDirect = () => {
    const { isUserInfoLoaded } = usePlatformContext();
    const { globalVariables } = useContext(AppContext);
    const { workspaceId: rawWorkspaceId } = useParams<RouteParams>();
    const location = useLocation();

    const workspaceEnum = parseWorkspaceId(rawWorkspaceId ?? null);
    const chainParam = useMemo(
        () => new URLSearchParams(location.search).get('chain'),
        [location.search],
    );
    const needsChain = workspaceEnum !== null && CHAIN_ENABLED_WORKSPACES.includes(workspaceEnum);

    const forcedChainFromParam = useMemo(() => {
        if (!needsChain || !chainParam) return undefined;

        const match = globalVariables.availableChains.find(c => equalInLC(c.name, chainParam));
        return match ? match.id : null;
    }, [needsChain, chainParam, globalVariables.availableChains]);

    const resolution = useWorkspaceResolution({
        mode: 'backdoor',
        forcedWorkspaceId: workspaceEnum ?? undefined,
        forcedChainId: forcedChainFromParam,
    });

    if (!isUserInfoLoaded) {
        return <div className="Home"/>;
    }

    const WorkspaceComponent = workspaceEnum ? WORKSPACE_COMPONENTS[workspaceEnum] : undefined;
    const chainParamInvalid = needsChain && !!chainParam && forcedChainFromParam === null;

    if (!workspaceEnum || !WorkspaceComponent || chainParamInvalid || !resolution.isReady) {
        const reason = !workspaceEnum ? `unknown workspace "${rawWorkspaceId}"`
            : !WorkspaceComponent ? `no variation implemented for "${workspaceEnum}"`
            : chainParamInvalid ? `unknown chain "${chainParam}"`
            : 'resolver not ready';
        console.error('[WorkspaceDirect] falling back to LegacyWorkspace:', reason);
        return <LegacyWorkspace/>;
    }

    return (
        <HomeWorkspaceProvider
            key={`${workspaceEnum}-${resolution.chainId ?? 'none'}`}
            workspaceId={workspaceEnum}
            chainId={resolution.chainId}
            chainName={resolution.chainName}
            defaultVisibleSections={WORKSPACE_DEFAULT_SECTIONS[workspaceEnum]}
        >
            <WorkspaceComponent/>
        </HomeWorkspaceProvider>
    );
};

export default WorkspaceDirect;
